import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as api from "../lib/supabase";

export default function DriverMode({
  currentUser,
  orgId,
  organization,
  onMenuOpen,
}) {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [driverCostCodes, setDriverCostCodes] = useState([]);
  const [clockedIn, setClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [stops, setStops] = useState([]); // { id, jobId, jobName, jobNumber, hours, notes, costCodeId, costCode }
  const [showAddStop, setShowAddStop] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedCostCode, setSelectedCostCode] = useState(null);
  const [stopHours, setStopHours] = useState("");
  const [stopNotes, setStopNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [driverTimeEntry, setDriverTimeEntry] = useState(null);

  // Local date, not toISOString() — UTC would roll evening shifts to tomorrow
  const today = new Date().toLocaleDateString("en-CA");

  useEffect(() => {
    loadData();
  }, [orgId, currentUser?.id]); // loadData is stable - defined in component scope

  const loadData = async () => {
    setLoading(true);
    try {
      // Load active jobs
      const jobsData = await api.getActiveJobs(orgId);
      setJobs(jobsData || []);

      // Load driver cost codes from organization settings
      const driverCostCodeIds =
        organization?.settings?.driver_cost_code_ids || [];
      if (driverCostCodeIds.length > 0) {
        const allCostCodes = await api.getCostCodes(orgId);
        const driverCodes = allCostCodes.filter((cc) =>
          driverCostCodeIds.includes(cc.id),
        );
        setDriverCostCodes(driverCodes);
        // Auto-select first cost code if only one
        if (driverCodes.length === 1) {
          setSelectedCostCode(driverCodes[0]);
        }
      } else {
        // Fallback to driver_cost_code_id if no driver_cost_code_ids set
        if (organization?.driver_cost_code_id) {
          const allCostCodes = await api.getCostCodes(orgId);
          const defaultCode = allCostCodes.find(
            (cc) => cc.id === organization.driver_cost_code_id,
          );
          if (defaultCode) {
            setDriverCostCodes([defaultCode]);
            setSelectedCostCode(defaultCode);
          }
        }
      }

      // Check if driver has a time entry for today (clocked in)
      const existingEntry = await api.getDriverTimeEntry(
        orgId,
        currentUser.id,
        today,
      );
      if (existingEntry) {
        setDriverTimeEntry(existingEntry);
        setClockedIn(true);
        setClockInTime(existingEntry.clock_in);

        // Load existing stops/allocations
        if (existingEntry.allocations?.length > 0) {
          const existingStops = existingEntry.allocations.map((alloc) => ({
            id: alloc.id,
            jobId: alloc.job_id,
            jobName: alloc.job?.name || "Unknown Job",
            jobNumber: alloc.job?.job_number || "???",
            hours: alloc.hours,
            notes: alloc.notes || "",
          }));
          setStops(existingStops);
        }
      }
    } catch (error) {
      console.error("Error loading driver data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    try {
      setSaving(true);
      const now = new Date().toISOString();

      const entry = await api.createDriverTimeEntry({
        org_id: orgId,
        employee_id: currentUser.id,
        work_date: today,
        clock_in: now,
        entry_type: "driver",
      });

      setDriverTimeEntry(entry);
      setClockedIn(true);
      setClockInTime(now);
    } catch (error) {
      console.error("Error clocking in:", error);
      toast.error("Failed to clock in. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleClockOut = async () => {
    if (!driverTimeEntry) return;

    try {
      setSaving(true);
      const now = new Date().toISOString();

      const clockIn = new Date(clockInTime);
      const clockOut = new Date(now);
      const totalHours = (clockOut - clockIn) / (1000 * 60 * 60);

      await api.updateDriverTimeEntry(driverTimeEntry.id, {
        clock_out: now,
        total_hours: Math.round(totalHours * 100) / 100,
      });

      setClockedIn(false);
      setClockInTime(null);
      setDriverTimeEntry(null);
      setStops([]);
    } catch (error) {
      console.error("Error clocking out:", error);
      toast.error("Failed to clock out. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddStop = async () => {
    if (!selectedJob || !stopHours || !driverTimeEntry) return;

    // Need a cost code if multiple are available
    const costCodeToUse = selectedCostCode || driverCostCodes[0] || null;
    const costCodeId = costCodeToUse?.id || organization?.driver_cost_code_id;

    try {
      setSaving(true);

      const allocation = await api.createDriverAllocation({
        time_entry_id: driverTimeEntry.id,
        job_id: selectedJob.id,
        cost_code_id: costCodeId,
        hours: parseFloat(stopHours),
        notes: stopNotes,
      });

      setStops([
        ...stops,
        {
          id: allocation.id,
          jobId: selectedJob.id,
          jobName: selectedJob.name,
          jobNumber: selectedJob.job_number,
          hours: parseFloat(stopHours),
          notes: stopNotes,
          costCodeId: costCodeId,
          costCode: costCodeToUse?.code || "",
        },
      ]);

      // Reset form
      setSelectedJob(null);
      setStopHours("");
      setStopNotes("");
      // Don't reset cost code if only one option
      if (driverCostCodes.length > 1) {
        setSelectedCostCode(null);
      }
      setShowAddStop(false);
    } catch (error) {
      console.error("Error adding stop:", error);
      toast.error("Failed to add stop. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStop = async (stopId) => {
    try {
      setSaving(true);
      await api.deleteDriverAllocation(stopId);
      setStops(stops.filter((s) => s.id !== stopId));
    } catch (error) {
      console.error("Error deleting stop:", error);
      toast.error("Failed to delete stop.");
    } finally {
      setSaving(false);
    }
  };

  const totalAllocatedHours = stops.reduce((sum, s) => sum + s.hours, 0);

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getCurrentHours = () => {
    if (!clockInTime) return 0;
    const start = new Date(clockInTime);
    const now = new Date();
    return Math.round(((now - start) / (1000 * 60 * 60)) * 10) / 10;
  };

  const getUnallocatedHours = () => {
    return Math.round((getCurrentHours() - totalAllocatedHours) * 10) / 10;
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", color: "#64748b" }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        paddingBottom: clockedIn ? "100px" : "0",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#1a5a8e",
          color: "white",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={onMenuOpen}
            style={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "-8px",
              borderRadius: "8px",
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
              fontSize: "20px",
            }}
          >
            ☰
          </button>
          <div>
            <h1 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
              Driver Mode
            </h1>
            <div style={{ fontSize: "13px", opacity: 0.8 }}>
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Not Clocked In State */}
      {!clockedIn && (
        <div style={{ padding: "40px 16px", textAlign: "center" }}>
          <div
            style={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #1a5a8e 0%, #2d7ab8 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              fontSize: "48px",
            }}
          >
            🚛
          </div>
          <h2 style={{ margin: "0 0 8px", color: "#1e293b", fontSize: "22px" }}>
            Ready to Start?
          </h2>
          <p style={{ margin: "0 0 32px", color: "#64748b", fontSize: "15px" }}>
            Clock in to start tracking your deliveries
          </p>
          <button
            onClick={handleClockIn}
            disabled={saving}
            style={{
              width: "100%",
              maxWidth: "300px",
              padding: "18px 24px",
              background: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "16px",
              fontSize: "18px",
              fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)",
            }}
          >
            {saving ? "Clocking In..." : "Clock In"}
          </button>
        </div>
      )}

      {/* Clocked In State */}
      {clockedIn && (
        <>
          {/* Summary Bar */}
          <div
            style={{
              padding: "16px",
              background: "white",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-around",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#10b981",
                }}
              >
                {formatTime(clockInTime)}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Clocked In
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#1e293b",
                }}
              >
                {getCurrentHours()}h
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Total Time
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#1a5a8e",
                }}
              >
                {totalAllocatedHours.toFixed(1)}h
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Allocated
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: getUnallocatedHours() > 0 ? "#f59e0b" : "#10b981",
                }}
              >
                {getUnallocatedHours()}h
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Remaining
              </div>
            </div>
          </div>

          {/* Stops Section Header */}
          <div
            style={{
              padding: "12px 16px",
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#64748b",
                textTransform: "uppercase",
              }}
            >
              Job Stops ({stops.length})
            </div>
          </div>

          {/* Stops List */}
          {stops.length === 0 ? (
            <div style={{ padding: "40px 16px", textAlign: "center" }}>
              <div style={{ fontSize: "40px", marginBottom: "12px" }}>📍</div>
              <p style={{ margin: 0, color: "#64748b", fontSize: "15px" }}>
                No stops yet. Tap the button below to add your first delivery!
              </p>
            </div>
          ) : (
            <div>
              {stops.map((stop) => (
                <div
                  key={stop.id}
                  style={{
                    background: "white",
                    borderBottom: "1px solid #f1f5f9",
                    padding: "14px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "12px",
                      background: "#e8f4fc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#1a5a8e",
                      fontWeight: "700",
                      fontSize: "14px",
                      flexShrink: 0,
                    }}
                  >
                    {stop.jobNumber}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#1e293b",
                      }}
                    >
                      {stop.jobName}
                    </div>
                    {stop.notes && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#64748b",
                          marginTop: "2px",
                        }}
                      >
                        {stop.notes}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      background: "#f0fdf4",
                      padding: "6px 12px",
                      borderRadius: "8px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#10b981",
                      }}
                    >
                      {stop.hours}h
                    </div>
                  </div>

                  <button
                    onClick={() => handleDeleteStop(stop.id)}
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      background: "#fee2e2",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#dc2626",
                      fontSize: "18px",
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Bottom Action Bar */}
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              background: "white",
              borderTop: "1px solid #e2e8f0",
              padding: "12px 16px",
              paddingBottom: "28px",
              display: "flex",
              gap: "12px",
              boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
              maxWidth: "430px",
              margin: "0 auto",
            }}
          >
            <button
              onClick={handleClockOut}
              disabled={saving}
              style={{
                padding: "14px 16px",
                borderRadius: "12px",
                border: "none",
                background: "#fee2e2",
                fontSize: "15px",
                fontWeight: "600",
                color: "#dc2626",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              Clock Out
            </button>
            <button
              onClick={() => setShowAddStop(true)}
              style={{
                flex: 1,
                padding: "14px 20px",
                borderRadius: "12px",
                border: "none",
                background: "#1a5a8e",
                fontSize: "15px",
                fontWeight: "600",
                color: "white",
                cursor: "pointer",
              }}
            >
              + Add Job Stop
            </button>
          </div>
        </>
      )}

      {/* Add Stop Modal */}
      {showAddStop && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={() => {
              setShowAddStop(false);
              setSelectedJob(null);
              setStopHours("");
              setStopNotes("");
            }}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "430px",
              margin: "0 auto",
              background: "white",
              borderRadius: "24px 24px 0 0",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ padding: "12px 24px 0" }}>
              <div
                style={{
                  width: "40px",
                  height: "4px",
                  background: "#cbd5e1",
                  borderRadius: "2px",
                  margin: "0 auto",
                }}
              />
            </div>

            <div
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                Add Job Stop
              </div>
              <div
                style={{ fontSize: "14px", color: "#64748b", marginTop: "2px" }}
              >
                Select job and enter time spent
              </div>
            </div>

            <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
              {/* Hours Selection */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Hours
                </label>
                <div
                  style={{ display: "flex", gap: "6px", marginBottom: "8px" }}
                >
                  {[0.5, 1, 1.5, 2, 3, 4].map((h) => (
                    <button
                      key={h}
                      onClick={() => setStopHours(h.toString())}
                      style={{
                        flex: 1,
                        padding: "14px 4px",
                        borderRadius: "10px",
                        border:
                          stopHours === h.toString()
                            ? "2px solid #1a5a8e"
                            : "1px solid #e2e8f0",
                        background:
                          stopHours === h.toString() ? "#e8f4fc" : "white",
                        fontSize: "16px",
                        fontWeight: "600",
                        color:
                          stopHours === h.toString() ? "#1a5a8e" : "#1e293b",
                        cursor: "pointer",
                      }}
                    >
                      {h}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  step="0.5"
                  placeholder="Custom hours..."
                  value={stopHours}
                  onChange={(e) => setStopHours(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    fontSize: "16px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Job Selection */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Select Job
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    maxHeight: "250px",
                    overflowY: "auto",
                  }}
                >
                  {jobs.map((job) => (
                    <button
                      key={job.id}
                      onClick={() => setSelectedJob(job)}
                      style={{
                        padding: "12px",
                        borderRadius: "10px",
                        border:
                          selectedJob?.id === job.id
                            ? "2px solid #1a5a8e"
                            : "1px solid #e2e8f0",
                        background:
                          selectedJob?.id === job.id ? "#e8f4fc" : "white",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "600",
                          color:
                            selectedJob?.id === job.id ? "#1a5a8e" : "#1e293b",
                        }}
                      >
                        {job.job_number}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#64748b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {job.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Notes (optional)
                </label>
                <input
                  type="text"
                  value={stopNotes}
                  onChange={(e) => setStopNotes(e.target.value)}
                  placeholder="e.g., 2 loads of block"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    fontSize: "16px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Cost Code Selection - only show if multiple options */}
              {driverCostCodes.length > 1 && (
                <div style={{ marginTop: "20px" }}>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Cost Code
                  </label>
                  <div
                    style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                  >
                    {driverCostCodes.map((cc) => (
                      <button
                        key={cc.id}
                        onClick={() => setSelectedCostCode(cc)}
                        style={{
                          padding: "10px 16px",
                          borderRadius: "10px",
                          border:
                            selectedCostCode?.id === cc.id
                              ? "2px solid #1a5a8e"
                              : "1px solid #e2e8f0",
                          background:
                            selectedCostCode?.id === cc.id
                              ? "#e8f4fc"
                              : "white",
                          fontSize: "14px",
                          fontWeight: "600",
                          color:
                            selectedCostCode?.id === cc.id
                              ? "#1a5a8e"
                              : "#1e293b",
                          cursor: "pointer",
                        }}
                      >
                        {cc.code} {cc.description && `- ${cc.description}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                padding: "16px 24px",
                paddingBottom: "28px",
                borderTop: "1px solid #e2e8f0",
                background: "white",
              }}
            >
              <button
                onClick={handleAddStop}
                disabled={!selectedJob || !stopHours || saving}
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "none",
                  background:
                    !selectedJob || !stopHours ? "#e2e8f0" : "#10b981",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: !selectedJob || !stopHours ? "#94a3b8" : "white",
                  cursor:
                    !selectedJob || !stopHours || saving
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {saving
                  ? "Adding..."
                  : selectedJob && stopHours
                    ? `+ Add ${stopHours}h to ${selectedJob.job_number}`
                    : "Select hours & job"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
